// ── Design decisions ──
// La Producción es el "recibo" final del viaje: sólo tiene sentido cuando el
// manifiesto está cerrado, porque antes los números cambian con cada ítem.
// Por eso un viaje en borrador muestra un estado vacío cálido que manda al
// operador a la pestaña Manifiesto, y nunca la tabla (evita que se imprima o
// exporte un parcial por error).
//
// · Los números salen de computeProduccion (puro): acá no se calcula nada,
//   sólo se formatea con fmtUsd. Cada fila es un paquete con su R, IVA y DAI.
// · Paquetes sin tipo se avisan arriba en ámbar: nunca se ocultan filas. Sin
//   config aduanal completa se avisa en rojo y los impuestos quedan en cero.
// · Exportar XLSX y Generar PDF son independientes: cada botón tiene su
//   propio "Generando…", su error inline (role="alert") y su "Listo:
//   <archivo>". El toast de éxito lo dispara el contenedor.
// · Reabrir es terminal → Modal con confirmación explícita, borde ámbar para
//   diferenciarlo de las acciones de salida.

import { useMemo, useState } from 'react';
import { Boxes, FileSpreadsheet, FileText, Package, RotateCcw, ShoppingBag } from 'lucide-react';
import { computeProduccion } from './produccionCalc';
import Modal from './shared/Modal';
import { TipoIngresoChip } from './shared/chips';
import { fmtUsd } from './shared/labels';

const th = 'whitespace-nowrap px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500';
const thNum = `${th} text-right`;
const td = 'whitespace-nowrap px-3 py-2 text-xs text-slate-700';
const tdNum = `${td} text-right font-mono`;

const SummaryCard = ({ icon: Icon, label, value }) => (
  <div role="group" aria-label={label} className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-3">
    <span className="rounded-md bg-[var(--brand-teal-soft)] p-2 text-slate-700">
      <Icon size={16} aria-hidden="true" />
    </span>
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="text-lg font-semibold text-slate-900">{value}</p>
    </div>
  </div>
);

// Botón de acción asíncrona con estado propio (pendiente / error / listo).
const AccionBoton = ({ icon: Icon, label, pendienteLabel, onRun, className }) => {
  const [pendiente, setPendiente] = useState(false);
  const [error, setError] = useState('');
  const [listo, setListo] = useState('');

  const run = async () => {
    if (pendiente) return;
    setPendiente(true);
    setError('');
    setListo('');
    try {
      const res = await onRun?.();
      setListo(res?.filename ? `Listo: ${res.filename}` : 'Listo.');
    } catch (e) {
      setError(e?.message || 'No se pudo completar la acción.');
    } finally {
      setPendiente(false);
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        onClick={run}
        disabled={pendiente}
        className={`inline-flex min-h-[44px] items-center justify-center gap-2 rounded-md px-4 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-60 ${className}`}
      >
        <Icon size={14} aria-hidden="true" /> {pendiente ? pendienteLabel : label}
      </button>
      {listo && <span className="text-[11px] font-semibold text-green-700">{listo}</span>}
      {error && (
        <span role="alert" className="text-[11px] font-semibold text-red-600">
          {error}
        </span>
      )}
    </div>
  );
};

const ProduccionTab = ({ viaje, customers = [], taxConfig, onReabrir, onExportXlsx, onGenerarPdf }) => {
  const [modalReabrir, setModalReabrir] = useState(false);
  const [reabriendo, setReabriendo] = useState(false);
  const [errorReabrir, setErrorReabrir] = useState('');

  const cerrado = viaje?.status === 'cerrado';
  const { filas, totales, resumen } = useMemo(
    () => computeProduccion(viaje, taxConfig, customers),
    [viaje, taxConfig, customers]
  );

  const cerrarModal = () => {
    if (reabriendo) return;
    setModalReabrir(false);
    setErrorReabrir('');
  };

  const confirmarReabrir = async () => {
    if (reabriendo) return;
    setReabriendo(true);
    setErrorReabrir('');
    try {
      await onReabrir?.();
      setModalReabrir(false);
    } catch (e) {
      setErrorReabrir(e?.message || 'No se pudo reabrir el manifiesto.');
    } finally {
      setReabriendo(false);
    }
  };

  if (!cerrado) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 bg-[#f8fafc] px-6 py-12 text-center">
        <span className="rounded-full bg-[var(--brand-teal-soft)] p-4 text-slate-700">
          <FileText size={28} aria-hidden="true" />
        </span>
        <p className="max-w-md text-sm font-semibold text-slate-800">
          El manifiesto sigue abierto. Cerralo desde la pestaña Manifiesto para ver la producción final.
        </p>
        <p className="max-w-md text-xs text-slate-500">
          Vista previa: {resumen.paquetes} paquetes · {resumen.articulos} artículos · FOB ${fmtUsd(resumen.fob)}.
          Los impuestos se calculan al cerrar.
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col overflow-y-auto bg-[#f8fafc] px-4 py-3">
      <div className="mb-3 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <SummaryCard icon={Package} label="Total paquetes" value={resumen.paquetes} />
        <SummaryCard icon={Boxes} label="Total bultos" value={resumen.bultos} />
        <SummaryCard icon={ShoppingBag} label="Total artículos" value={resumen.articulos} />
        <SummaryCard icon={FileText} label="FOB total" value={`$${fmtUsd(resumen.fob)}`} />
      </div>

      {resumen.sinConfig && (
        <p role="alert" className="mb-3 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs font-semibold text-red-800">
          Falta la configuración aduanal de encomienda — no se puede calcular el impuesto.
        </p>
      )}

      {resumen.sinTipo > 0 && (
        <p className="mb-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">
          {resumen.sinTipo === 1
            ? '1 paquete sin tipo de ingreso.'
            : `${resumen.sinTipo} paquetes sin tipo de ingreso.`}
        </p>
      )}

      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="w-full min-w-[960px] border-collapse">
          <caption className="px-3 py-2 text-left text-xs font-semibold text-slate-700">Producción por paquete</caption>
          <thead className="border-b border-slate-200 bg-slate-50">
            <tr>
              <th scope="col" className={th}>Cliente</th>
              <th scope="col" className={th}>Tipo de ingreso</th>
              <th scope="col" className={thNum}>FOB</th>
              <th scope="col" className={thNum}>R (FOB + flete + seguro)</th>
              <th scope="col" className={thNum}>Umbral</th>
              <th scope="col" className={thNum}>IVA</th>
              <th scope="col" className={thNum}>DAI</th>
              <th scope="col" className={thNum}>Total impuestos</th>
            </tr>
          </thead>
          <tbody>
            {filas.map((f) => (
              <tr key={f.paqueteId} className="border-b border-slate-100 last:border-b-0" data-testid={`fila-${f.paqueteId}`}>
                <td className={`${td} font-semibold text-slate-900`}>{f.cliente || '—'}</td>
                <td className={td}>
                  <TipoIngresoChip value={f.tipo_ingreso} />
                </td>
                <td className={tdNum}>{fmtUsd(f.fob)}</td>
                <td className={tdNum}>{fmtUsd(f.r)}</td>
                <td className={tdNum}>{f.umbral == null ? '—' : fmtUsd(f.umbral)}</td>
                <td className={tdNum}>{fmtUsd(f.iva)}</td>
                <td className={tdNum}>{fmtUsd(f.dai)}</td>
                <td className={`${tdNum} font-semibold text-slate-900`}>{fmtUsd(f.total)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="border-t-2 border-slate-300 bg-slate-50">
            <tr data-testid="fila-totales">
              <th scope="row" colSpan={2} className={`${td} text-left font-semibold text-slate-900`}>
                Totales
              </th>
              <td className={`${tdNum} font-semibold`}>{fmtUsd(totales.fob)}</td>
              <td className={`${tdNum} font-semibold`}>{fmtUsd(totales.r)}</td>
              <td className={tdNum}>—</td>
              <td className={`${tdNum} font-semibold`}>{fmtUsd(totales.iva)}</td>
              <td className={`${tdNum} font-semibold`}>{fmtUsd(totales.dai)}</td>
              <td className={`${tdNum} font-semibold text-slate-900`}>{fmtUsd(totales.total)}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="mt-4 flex flex-wrap items-start gap-3">
        <AccionBoton
          icon={FileSpreadsheet}
          label="Descargar XLSX"
          pendienteLabel="Generando…"
          onRun={onExportXlsx}
          className="bg-slate-900 text-white hover:bg-slate-800"
        />
        <AccionBoton
          icon={FileText}
          label="Generar Anexo PDF"
          pendienteLabel="Generando…"
          onRun={onGenerarPdf}
          className="bg-slate-900 text-white hover:bg-slate-800"
        />
        <button
          type="button"
          onClick={() => setModalReabrir(true)}
          className="ml-auto inline-flex min-h-[44px] items-center gap-2 rounded-md border border-amber-400 bg-white px-4 text-sm font-semibold text-amber-800 hover:bg-amber-50"
        >
          <RotateCcw size={14} aria-hidden="true" /> Reabrir manifiesto
        </button>
      </div>

      <Modal
        open={modalReabrir}
        title="Reabrir manifiesto"
        onClose={cerrarModal}
        actions={
          <>
            <button
              type="button"
              onClick={cerrarModal}
              disabled={reabriendo}
              className="min-h-[44px] rounded-md border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={confirmarReabrir}
              disabled={reabriendo}
              className="min-h-[44px] rounded-md border border-amber-400 bg-amber-50 px-4 text-sm font-semibold text-amber-900 hover:bg-amber-100 disabled:opacity-50"
            >
              {reabriendo ? 'Reabriendo…' : 'Sí, reabrir'}
            </button>
          </>
        }
      >
        <p>
          El viaje vuelve a borrador y se puede editar. Los totales de Producción se recalculan al volver a cerrar.
        </p>
        {errorReabrir && (
          <p role="alert" className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            {errorReabrir}
          </p>
        )}
      </Modal>
    </div>
  );
};

export default ProduccionTab;
